'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryKey } from '@tanstack/react-query';
import { getDependencies } from '@/lib/services/query-dependencies';
import { FRESH_DATA_CONFIG } from '../query-config';
import {
  editPrecedentDocumentInWord,
  generatePrecedentDocument,
  getPrecedentDocumentOptions,
  getPrecedentDocuments,
  refreshPrecedentDocumentFromWord,
  regeneratePrecedentDocument
} from './client';
import type { GeneratePrecedentDocumentInput } from './types';

export const precedentDocumentKeys = {
  all: ['precedent-documents'] as const,
  /** The documents generated on a matter. */
  list: (matterId: number) => [...precedentDocumentKeys.all, matterId] as const,
  /**
   * The generate dialog's inputs — a **separate** key, deliberately not folded
   * into the list. The options read loads every candidate precedent's bytes to
   * discover its clause toggles, so it is markedly more expensive than listing
   * what has already been generated; the tab reads the list on every visit and
   * the options only when the dialog opens.
   */
  options: (matterId: number) =>
    [...precedentDocumentKeys.all, matterId, 'options'] as const
};

/** Every document generated from the firm's own precedents on this matter. */
export function usePrecedentDocuments(matterId: number, enabled = true) {
  return useQuery({
    queryKey: precedentDocumentKeys.list(matterId),
    queryFn: () => getPrecedentDocuments(matterId),
    enabled: enabled && Number.isFinite(matterId),
    ...FRESH_DATA_CONFIG
  });
}

/** The generate dialog's inputs. Fetched only while the dialog is open — see the key note. */
export function usePrecedentDocumentOptions(
  matterId: number,
  enabled: boolean
) {
  return useQuery({
    queryKey: precedentDocumentKeys.options(matterId),
    queryFn: () => getPrecedentDocumentOptions(matterId),
    enabled: enabled && Number.isFinite(matterId),
    ...FRESH_DATA_CONFIG
  });
}

/**
 * One invalidation list for every write, resolved through `query-dependencies`
 * rather than hand-rolled (LF-3021 single-source rule). Wider than this
 * surface's own key, because generating writes real `File` rows on the matter.
 */
function usePrecedentDocumentInvalidation(matterId: number) {
  const queryClient = useQueryClient();
  return () => {
    for (const queryKey of getDependencies(
      'precedentDocuments',
      matterId
    ) as QueryKey[]) {
      queryClient.invalidateQueries({ queryKey });
    }
  };
}

export function useGeneratePrecedentDocument(matterId: number) {
  const invalidate = usePrecedentDocumentInvalidation(matterId);
  return useMutation({
    mutationFn: (input: GeneratePrecedentDocumentInput) =>
      generatePrecedentDocument(matterId, input),
    onSuccess: invalidate
  });
}

/**
 * Render a generated document again, in place.
 *
 * Distinct from `useGeneratePrecedentDocument` in what it asks for, not in what
 * it does: the record already holds the precedent, the addressee, the clauses
 * and the exhibits, so regenerating needs only its id. That is the whole point
 * — this used to reopen the generate drawer prefilled with those answers and
 * wait for them to be confirmed.
 */
export function useRegeneratePrecedentDocument(matterId: number) {
  const invalidate = usePrecedentDocumentInvalidation(matterId);
  return useMutation({
    mutationFn: (documentId: number) =>
      regeneratePrecedentDocument(matterId, documentId),
    onSuccess: invalidate
  });
}

/**
 * Open a document in Word.
 *
 * Deliberately **does not** invalidate: nothing on the matter has changed yet —
 * the edit happens in Word, and the save-back arrives through the refresh
 * control or the change webhook, both of which invalidate then.
 */
export function useEditPrecedentDocumentInWord(matterId: number) {
  return useMutation({
    mutationFn: (documentId: number) =>
      editPrecedentDocumentInWord(matterId, documentId)
  });
}

/** Pull a committed Word edit in now. Invalidates — an ingest replaces the document's files. */
export function useRefreshPrecedentDocumentFromWord(matterId: number) {
  const invalidate = usePrecedentDocumentInvalidation(matterId);
  return useMutation({
    mutationFn: (documentId: number) =>
      refreshPrecedentDocumentFromWord(matterId, documentId),
    onSuccess: invalidate
  });
}
